import { useCallback } from 'react'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { authApi } from '@/domain/auth/authApi'
import { TelegramLinkDTO } from '@/domain/auth/authTypes'
import { useDeviceInfosStore } from '@/store'
import { useTelegramAuthStore } from '@/store/useTelegramAuthStore'
import { detectTelegramWebView } from '@/utils/telegram'
import { telegramAuthLogger } from '@/services/telegramAuthLogger'

interface LinkWithAccountParams {
  /** User's email (provide either email OR phone_number) */
  email?: string
  /** User's phone number (provide either email OR phone_number) */
  phoneNumber?: string
  /** User's password - passed directly, NOT stored anywhere */
  password: string
}

interface UseTelegramLinkResult {
  /** Whether we're in Telegram and have a telegram_id to link */
  canLink: boolean
  /** Get current canLink value (bypasses stale closure issues) */
  getCanLink: () => boolean
  /** Current telegram_id from store */
  telegramId: string | null
  /** Link telegram to email/phone account */
  linkWithAccount: (params: LinkWithAccountParams) => Promise<TelegramLinkDTO | null>
  /** Whether linking is in progress */
  isLinking: boolean
}

/**
 * Hook for linking Telegram account to existing user accounts
 *
 * Used after successful password verification or password setup
 * to link the user's Telegram ID with their Kem account.
 *
 * Security Note:
 * - Password is passed directly to the API, never stored
 * - telegram_id comes from the store (set during /telegram/initiate)
 * - Linking only happens if user is in Telegram environment
 */
export const useTelegramLink = (): UseTelegramLinkResult => {
  const { t } = useTranslation()

  // Store access
  const deviceInfos = useDeviceInfosStore()
  const telegramId = useTelegramAuthStore((s) => s.telegramId)
  const status = useTelegramAuthStore((s) => s.status)
  const setStatus = useTelegramAuthStore((s) => s.setStatus)
  const setError = useTelegramAuthStore((s) => s.setError)

  const isInTelegram = detectTelegramWebView()
  const isLinking = status === 'linking'

  // Can link if we're in Telegram and have a telegram_id that needs linking
  const canLink = isInTelegram && !!telegramId && status === 'needs_linking'

  /**
   * Get current canLink value by reading directly from store
   * Use this in callbacks to avoid stale closure issues
   */
  const getCanLink = useCallback((): boolean => {
    const currentState = useTelegramAuthStore.getState()
    const currentCanLink = isInTelegram && !!currentState.telegramId && currentState.status === 'needs_linking'
    return currentCanLink
  }, [isInTelegram])

  /**
   * Links Telegram account to an existing email/phone account
   *
   * Called after successful password verification or setup.
   * Password is passed directly - it's only stored in component state.
   *
   * @param params - Email/phone and password
   * @returns TelegramLinkDTO with tokens on success, null on failure
   */
  const linkWithAccount = useCallback(
    async (params: LinkWithAccountParams): Promise<TelegramLinkDTO | null> => {
      const { email, phoneNumber, password } = params

      // Validate we can link
      if (!isInTelegram) {
        return null
      }

      if (!telegramId) {
        return null
      }

      if (status !== 'needs_linking') {
        return null
      }

      if (!email && !phoneNumber) {
        return null
      }

      setStatus('linking')
      setError(null)

      try {
        // Prepare payload
        const payload = {
          telegram_id: telegramId,
          device_token: deviceInfos.device_id || '',
          app_version: deviceInfos.app_version || '1.0.0',
          platform: 'WEB' as const,
          language: deviceInfos.language || 'en',
          email: email || undefined,
          phone_number: phoneNumber || undefined,
          password,
        }

        // Log the request (password not logged)
        telegramAuthLogger.logTelegramLink({
          telegramId: payload.telegram_id,
          hasEmail: !!payload.email,
          hasPhoneNumber: !!payload.phone_number,
          platform: payload.platform,
          language: payload.language,
        })

        // Call API
        const response = await authApi.telegramLinkAccount(payload)

        // Log the response
        telegramAuthLogger.logTelegramLinkResponse({
          success: true,
          hasAccessToken: !!response.access_token,
          hasRefreshToken: !!response.refresh_token,
        })

        // Show success toast
        toast.success(t('screens:telegram.accountLinked', 'Telegram account linked successfully'))

        setStatus('authenticated')
        return response
      } catch (error) {
        telegramAuthLogger.logTelegramLinkError(error)

        const errorMessage = error instanceof Error ? error.message : 'Failed to link Telegram account'
        setError(errorMessage)
        setStatus('link_failed')

        // Show error toast - non-blocking, user can still proceed
        toast.error(t('screens:telegram.linkFailed', 'Could not link Telegram account'))

        // Return null but don't throw - linking failure shouldn't block login
        return null
      }
    },
    [isInTelegram, telegramId, status, deviceInfos, setStatus, setError, t],
  )

  return {
    canLink,
    getCanLink,
    telegramId,
    linkWithAccount,
    isLinking,
  }
}
